import { useState, useEffect } from 'react'
import { useAbm } from '../hooks/useAbm'
import Boton from '../components/Boton'
import api from '../api/client'
import { colors, text, space, sx, font, radius } from '../design/tokens'
import Paginador from '../components/Paginador'

/**
 * CLIENTES → Portal — gestión interna del portal del cliente.
 * Desde acá el equipo busca un cliente y le comparte su acceso:
 * ver el portal como lo ve él, copiar el link o mandárselo por mail.
 */
export default function PortalClientesAdminPage() {
  const { rows, loading, setPage, setSearch, meta, search } = useAbm('clientes', { paginated: true })
  const [searchInput, setSearchInput] = useState('')
  // Feedback en el propio botón (patrón ClientesPage): { id, accion: 'copiado'|'enviado' }
  const [hecho, setHecho] = useState(null)
  // Solo los errores se muestran como texto (caso excepcional)
  const [errorFila, setErrorFila] = useState(null) // { id, msg }

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) setSearch(searchInput) }, 350)
    return () => clearTimeout(t)
  }, [searchInput, search, setSearch])

  const marcarHecho = (id, accion) => {
    setErrorFila(null)
    setHecho({ id, accion })
    setTimeout(() => setHecho(h => (h?.id === id && h?.accion === accion ? null : h)), 1500)
  }
  const marcarError = (id, msg) => {
    setErrorFila({ id, msg })
    setTimeout(() => setErrorFila(e => (e?.id === id ? null : e)), 2500)
  }

  const verPortal = async (row) => {
    try {
      const { data } = await api.get(`/clientes/${row.id}/link-portal`)
      window.open(data.url, '_blank')
    } catch { marcarError(row.id, 'No se pudo generar el link.') }
  }

  const copiarLink = async (row) => {
    try {
      const { data } = await api.get(`/clientes/${row.id}/link-portal`)
      await navigator.clipboard.writeText(data.url)
      marcarHecho(row.id, 'copiado')
    } catch { marcarError(row.id, 'No se pudo generar el link.') }
  }

  const enviarMail = async (row) => {
    try {
      await api.post(`/clientes/${row.id}/enviar-portal`)
      marcarHecho(row.id, 'enviado')
    } catch (err) {
      marcarError(row.id, err.response?.data?.message || 'No se pudo enviar.')
    }
  }

  return (
    <div>
      <div style={st.headerRow}>
        <div>
          <h2 style={st.title}>Portal de clientes</h2>
          <p style={st.subtitle}>
            Compartile a cada cliente su acceso: ahí ve sus envíos en tiempo real y todos sus comprobantes.
            El cliente también puede pedirlo solo en <code style={sx.mono}>/portal</code> con su email.
          </p>
        </div>
      </div>

      <div style={{ ...sx.card, padding: 0, overflow: 'hidden' }}>
        <div style={st.toolbar}>
          <input
            style={st.search}
            placeholder="Buscar por nombre, código, CUIT, email…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          {meta && <span style={st.metaTxt}>{meta.total} clientes</span>}
        </div>

        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Código</th>
              <th style={sx.th}>Nombre</th>
              <th style={sx.th}>Email</th>
              <th style={sx.th}>Portal</th>
              <th style={{ ...sx.th, textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={st.empty}>Cargando…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={st.empty}>Sin clientes para esa búsqueda</td></tr>
            ) : rows.map(row => (
              <tr key={row.id} style={st.tr}>
                <td style={{ ...sx.td, ...sx.mono }}>{row.codigo_presea || '—'}</td>
                <td style={{ ...sx.td, fontWeight: 500 }}>{row.nombre}</td>
                <td style={{ ...sx.td, color: row.email ? colors.text : colors.textPlh }}>{row.email || 'sin email'}</td>
                <td style={sx.td}>
                  {row.token_portal
                    ? <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>Activo</span>
                    : <span style={{ ...sx.badge, background: colors.bgSoft, color: colors.textSec }}>Sin generar</span>}
                </td>
                <td style={{ ...sx.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {errorFila?.id === row.id && (
                    <span style={st.errTxt}>{errorFila.msg}</span>
                  )}
                  <Boton variante="secondary" sm style={{ marginRight: 6 }} onClick={() => verPortal(row)}>
                    👁 Ver portal
                  </Boton>
                  <Boton variante="secondary" sm style={{ marginRight: 6 }} onClick={() => copiarLink(row)}>
                    {hecho?.id === row.id && hecho.accion === 'copiado' ? '✓ Copiado' : '🔗 Copiar'}
                  </Boton>
                  <Boton variante="send" sm disabled={!row.email} onClick={() => enviarMail(row)}
                    title={row.email ? `Mandar el acceso a ${row.email}` : 'El cliente no tiene email cargado'}>
                    {hecho?.id === row.id && hecho.accion === 'enviado' ? '✓ Enviado' : '✉ Enviar acceso'}
                  </Boton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && <Paginador pagina={meta.current_page} totalPaginas={meta.last_page} onCambiar={setPage} />}
      </div>
    </div>
  )
}

const st = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  subtitle: { margin: '4px 0 0', fontSize: text.body, color: colors.textSec, maxWidth: 640, lineHeight: 1.5 },
  toolbar: { display: 'flex', alignItems: 'center', gap: space.lg, padding: `${space.lg}px ${space.xl}px`, borderBottom: `1px solid ${colors.border}` },
  search: { flex: 1, maxWidth: 420, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.sans, outline: 'none' },
  metaTxt: { fontSize: text.meta, color: colors.textSec },
  tr: { height: 44, borderBottom: `1px solid ${colors.borderSoft}` },
  empty: { padding: `${space.xl}px`, textAlign: 'center', color: colors.textMeta, fontSize: text.body },
  errTxt: { marginRight: space.lg, fontSize: text.sec, fontWeight: 600, color: colors.err, verticalAlign: 'middle' },
  pager: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.xl, padding: `${space.lg}px` },
}
